<template>
    <div>
        <p
            v-if="contractFilterExists"
            class="mb-0"
        >
            <DTag
                color="danger"
                :label="__('customers.contracts.filters.contract_id_exists') + contractFilterExists"
            />
        </p>
        <TerminateContractsButton
            v-if="userPermissions.includes('contract.terminate') && customer.sanity === 'bankruptcy'"
            :customer="customer"
            :classes="'mt-1 mb-1'"
        />
        <div class="search align-items-end">
            <DFormRow
                :label="__('customers.contracts.filters.status')"
                is-vertical
                class="mb-0"
            >
                <DMultiSelect
                    id="statuses"
                    :options="statusOptions"
                    :selection="true"
                    :selected="selectedStatuses"
                    selection-type="text"
                    @update="updateSelectedStatuses"
                />
            </DFormRow>
            <DFormRow
                :label="__('customers.contracts.filters.type')"
                is-vertical
                class="mb-0"
            >
                <DSelect
                    id="type"
                    :options="typeOptions"
                    :search="true"
                    :selection="true"
                    :value="selectedType"
                    selection-type="text"
                    @update:value="updateSelectedType"
                />
            </DFormRow>
            <DFormRow
                :label="__('customers.contracts.filters.free_text')"
                is-vertical
                class="mb-0"
            >
                <DInput
                    id="free_text"
                    v-model="filter.freeText"
                    @keydown.enter.exact.prevent="submitFilters"
                />
            </DFormRow>
            <SearchOrderNumber
                class="search-order-number"
                :order-numbers="orderNumbers"
                :customer-id="customer.id"
                :order-from-filter="filter.orderNo"
                @selectOrder="selectOrder"
            />
            <DFormRow
                :label="__('customers.contracts.filters.show_fulfillment_package')"
                is-vertical
                class="mb-0"
            >
                <DSwitch
                    id="show_fulfillment_package"
                    color="success"
                    variant="3d"
                    style="margin: 0 0 5px 5px !important;"
                    :checked="filter.showFulfillmentPackage"
                    @update:checked="updateShowFulfillmentPackage"
                />
            </DFormRow>
            <DFormRow
                :label="__('contracts.contracts.product_group')"
                is-vertical
                class="mb-0"
            >
                <DMultiSelect
                    id="product-groups-dropdown"
                    :options="productGroupOptions"
                    :search="true"
                    :selection="true"
                    :selected="filter.productGroups"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedProductGroups"
                />
            </DFormRow>
        </div>
        <div class="search-btn align-items-end pt-3">
            <DButton
                id="filter-apply"
                class="me-1"
                :label="__('messages.actions.filter')"
                icon="filter"
                @click="submitFilters"
            />
            <DButton
                id="filter-reset"
                :label="__('messages.actions.reset_filter')"
                icon="filter-off"
                :disabled="!filterData"
                @click="resetFilters"
            />
        </div>
        <div class="customer-contracts">
            <div
                class="contracts-list"
                :class="{'border-0': contracts.length === 0, 'mt-4': contracts.length > 0}"
            >
                <ContractListItem
                    v-for="item in contracts"
                    :key="item.id"
                    :item="item"
                    :contract-filter-exists="contractFilterExists"
                    :is-child="item.parent_contract_id"
                    :customer="customer"
                    @navigateToSSO="navigateToSSO"
                    @clickActivate="showActivateContractModal"
                    @clickTerminate="terminateContract"
                    @clickTerminateSilently="terminateContractSilently"
                    @clickCancelTerminate="cancelContractTermination"
                    @clickApplyMoneyBackGuarantee="applyMoneyBackGuarantee"
                    @clickWithdrawal="withdrawContract"
                    @clickMoveContract="moveContract"
                    @clickCancelContract="cancelContract"
                    @clickUnsuspend="clickUnsuspend"
                    @clickRelease="clickRelease"
                    @clickReleaseTree="clickReleaseTree"
                    @clickSendAuthCode="clickSendAuthCode"
                    @clickDelete="clickDelete"
                    @clickDeleteTree="clickDeleteTree"
                    @clickRestore="clickRestore"
                    @clickAddAddon="clickAddAddon"
                />
            </div>
            <DPagination
                v-if="pagination.lastPage > 1"
                class="pb-2"
                :active-page="pagination.currentPage"
                :pages="pagination.lastPage"
                align="center"
                @update:activePage="changePage"
            />

            <DFabs>
                <s-s-o-fab
                    :customer="customer"
                    @open-modal="showCreateCloudpitCustomerModal = true"
                    @close-modal="showCreateCloudpitCustomerModal = false"
                />
                <DFab
                    v-if="userPermissions.includes('contract.create')"
                    id="btn-add"
                    icon="plus"
                    :label="__('customers.contracts.add')"
                    @click="$inertia.get($route('customers.customers.contracts.create', {customer: customer.id}))"
                />
                <DFab
                    v-if="userPermissions.includes('contract.create')"
                    id="btn-add-contract-tree"
                    icon="plus"
                    :label="__('customers.contracts.add_contract_tree')"
                    @click="$inertia.get($route('customers.customers.contracts.create-contract-tree', {customer: customer.id}))"
                />
            </DFabs>

            <DModal
                v-model:show="showSSOErrorModal"
                :title="__('customers.contracts.sso_error_title')"
                :message="ssoErrorMessage"
                is-error
                @close="closeSsoErrorModal"
            />
            <ActivateContractModal
                v-if="showActivateModal"
                :contract="selectedContract"
                :show-modal="showActivateModal"
                :redirect-to="redirectToWhenActivate"
                :filters="filter"
                @close-modal="showActivateModal = false"
            />
            <TerminateContractModal
                v-if="showTerminationModal"
                :contract="selectedContract"
                :show-modal="showTerminationModal"
                :termination-reasons="terminationReasons"
                :termination-dates="terminationDates"
                :errors-data="errors"
                :filters="filter"
                @close-modal="showTerminationModal = false"
                @update-termination-dates="updateTerminationDates"
            />
            <CancelContractTerminationModal
                v-if="showConfirmationModal"
                :contract="contractTermination"
                :show-modal="showConfirmationModal"
                :filters="filter"
                @close-modal="showConfirmationModal = false"
            />
            <MoneyBackGuaranteeModal
                v-if="showGuaranteeConfirmationModal"
                :contract="selectedContract"
                :show-modal="showGuaranteeConfirmationModal"
                :filters="filter"
                @close-modal="showGuaranteeConfirmationModal = false"
            />
            <WithdrawContractModal
                v-if="showWithdrawConfirmationModal"
                :contract="selectedContract"
                :show-modal="showWithdrawConfirmationModal"
                :filters="filter"
                @close-modal="showWithdrawConfirmationModal = false"
            />
            <MoveContractModal
                v-if="showMoveContractModal"
                :contract="selectedContract"
                :show-modal="showMoveContractModal"
                :filters="filter"
                @close-modal="showMoveContractModal = false"
            />
            <CreateCloudPitCustomerModal
                :customer="customer"
                :show-modal="showCreateCloudpitCustomerModal"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
            <TerminateSilentlyModal
                v-if="showTerminateSilentlyModal"
                :contract="selectedContract"
                :show-modal="showTerminateSilentlyModal"
                :filters="filter"
                @close-modal="showTerminateSilentlyModal = false"
            />
            <CancelContractModal
                :contract="selectedContract"
                :show-modal="showCancelContractModal"
                :errors-data="errors"
                :filters="filter"
                @close-modal="showCancelContractModal = false"
            />
            <UnsuspendContractModal
                :contract="selectedContract"
                :show-modal="showUnsuspendConfirmationModal"
                :filters="filter"
                @close-modal="showUnsuspendConfirmationModal = false"
            />
            <ReleaseContractModal
                :contract="selectedContract"
                :show-modal="showReleaseConfirmationModal"
                :filters="filter"
                @close-modal="showReleaseConfirmationModal = false"
            />
            <ReleaseTreeContractModal
                :contract="selectedContract"
                :show-modal="showReleaseTreeConfirmationModal"
                :message="releaseTreeModalMessage"
                :filters="filter"
                @close-modal="showReleaseTreeConfirmationModal = false"
            />
            <DeleteContractModal
                :contract="selectedContract"
                :show-modal="showDeleteConfirmationModal"
                :message="deleteModalMessage"
                :filters="filter"
                @close-modal="showDeleteConfirmationModal = false"
            />
            <DeleteTreeContractModal
                :contract="selectedContract"
                :show-modal="showDeleteTreeConfirmationModal"
                :error-message="deleteTreeModalErrorMessage"
                :message="deleteTreeModalMessage"
                :filters="filter"
                @close-modal="showDeleteTreeConfirmationModal = false"
            />
            <RestoreContract
                v-if="showRestoreContractModal"
                v-model:showModal="showRestoreContractModal"
                :filters="filter"
                :contract="selectedContract"
                redirect-to="index"
                @close-modal="closeRestoreTerminationModal"
            />
            <SendAuthCodeModal
                v-if="showSendAuthCodeModal"
                :contract="selectedContract"
                :show="showSendAuthCodeModal"
                :errors-data="errors"
                return-url="contract-list"
                @close-modal="showSendAuthCodeModal = false"
            />
        </div>
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'

import ContractListItem from '~@/Components/Customers/Contracts/ContractListItem'
import TerminateContractModal from '~@/Components/Customers/Contracts/Modals/TerminateContract'
import CancelContractTerminationModal from '~@/Components/Customers/Contracts/Modals/CancelContractTermination'
import MoneyBackGuaranteeModal from '~@/Components/Customers/Contracts/Modals/MoneyBackGuarantee'
import WithdrawContractModal from '~@/Components/Customers/Contracts/Modals/WithdrawContract'
import MoveContractModal from '~@/Components/Customers/Contracts/Modals/MoveContract'
import CancelContractModal from '~@/Components/Customers/Contracts/Modals/CancelContract'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import TerminateSilentlyModal from '~@/Components/Customers/Contracts/Modals/TerminateSilently'
import UnsuspendContractModal from '~@/Components/Customers/Contracts/Modals/UnsuspendContractModal'
import ReleaseContractModal from '~@/Components/Customers/Contracts/Modals/ReleaseContractModal'
import ReleaseTreeContractModal from '~@/Components/Customers/Contracts/Modals/ReleaseTreeContractModal'
import DeleteContractModal from '~@/Components/Customers/Contracts/Modals/DeleteContractModal'
import DeleteTreeContractModal from '~@/Components/Customers/Contracts/Modals/DeleteTreeContractModal'
import ActivateContractModal from '~@/Components/Customers/Contracts/Modals/ActivateContract.vue'
import SearchOrderNumber from '~@/Components/Customers/Contracts/SearchOrderNumber.vue'
import RestoreContract from '~@/Components/Customers/Contracts/Modals/RestoreContract.vue'
import TerminateContractsButton from '~@/Components/Customers/Contracts/TerminateContractsButton.vue'
import SendAuthCodeModal from '~@/Components/Customers/Contracts/Modals/SendAuthCode.vue'

export default {
    name: 'CustomerContractIndex',
    components: {
        TerminateContractsButton,
        RestoreContract,
        DeleteTreeContractModal,
        DeleteContractModal,
        ReleaseTreeContractModal,
        ReleaseContractModal,
        UnsuspendContractModal,
        TerminateSilentlyModal,
        CreateCloudPitCustomerModal,
        SSOFab,
        ContractListItem,
        TerminateContractModal,
        CancelContractTerminationModal,
        MoneyBackGuaranteeModal,
        WithdrawContractModal,
        MoveContractModal,
        CancelContractModal,
        ActivateContractModal,
        SearchOrderNumber,
        SendAuthCodeModal
    },
    layout: [App, CustomerBase],
    props: {
        contracts: {
            type: Array,
            default: () => { return [] }
        },
        pagination: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        terminationReasons: {
            type: Array,
            default: () => { return [] }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        filterData: {
            type: Object,
            default () {
                return {}
            }
        },
        orderNumbers: {
            type: Array,
            default: () => { return [] }
        },
        productGroups: {
            type: Array,
            default: () => {
                return []
            }
        },
        removeAllFilterStatuses: {
            type: Boolean,
            default: () => {
                return false
            }
        },
        applicableStatuses: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    data () {
        return {
            fields: [
                { key: 'id', label: this.__('customers.contracts.contract_id') },
                { key: 'parent_contract_id', label: this.__('customers.contracts.parent_contract_id') },
                { key: 'contract_number', label: this.__('customers.contracts.contract_number') },
                { key: 'product_name', label: this.__('customers.contracts.product_name') },
                { key: 'date', label: this.__('customers.contracts.valid_period') },
                { key: 'status', label: this.__('customers.contracts.status') },
                { key: 'actions', label: '' }
            ],
            selectedContract: null,
            sendWithdrawalEmail: false,
            showConfirmationModal: false,
            contractTermination: null,
            showTerminationModal: false,
            showSSOErrorModal: false,
            ssoErrorMessage: '',
            loginPageRedirectLink: null,
            showGuaranteeConfirmationModal: false,
            showWithdrawConfirmationModal: false,
            showMoveContractModal: false,
            terminationDates: [],
            showCreateCloudpitCustomerModal: false,
            showTerminateSilentlyModal: false,
            showCancelContractModal: false,
            showUnsuspendConfirmationModal: false,
            showReleaseConfirmationModal: false,
            showReleaseTreeConfirmationModal: false,
            showDeleteConfirmationModal: false,
            showDeleteTreeConfirmationModal: false,
            showActivateModal: false,
            typeOptions: [
                { value: '', label: '' },
                { value: 'subscription', label: this.__('customers.contracts.filters.types.subscription') },
                { value: 'one_time', label: this.__('customers.contracts.filters.types.one_time') }
            ],
            filter: {
                status: (this.filterData !== null && this.filterData.status) || this.getParamArray('status'),
                type: (this.filterData !== null && this.filterData.type) || this.getParam('type'),
                contract: (this.filterData !== null && this.filterData.contract) || this.getParam('contract'),
                freeText: (this.filterData !== null && this.filterData.freeText) || this.getParam('freeText'),
                orderNo: [this.filterData.orderNo] ?? [],
                showFulfillmentPackage: this.filterData.showFulfillmentPackage ?? false,
                productGroups: this.filterData.productGroups ?? []
            },
            numberOfChildContractWithStatusDraft: 0,
            releaseTreeModalMessage: null,
            deleteModalMessage: null,
            deleteTreeModalErrorMessage: null,
            deleteTreeModalMessage: null,
            numberOfChildContractWithPreLiveStatus: 0,
            redirectToWhenActivate: 'contract-list',
            showRestoreContractModal: false,
            removeAllFilterStatusesData: this.removeAllFilterStatuses ?? false,
            showSendAuthCodeModal: false
        }
    },
    computed: {
        statusOptions () {
            return this.applicableStatuses.map((status) => {
                return {
                    ...status,
                    selected: this.selectedStatuses.includes(status.value)
                }
            })
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        selectedStatuses () {
            if (!this.filter.status) return []

            return this.filter.status.map((item) => item)
        },
        selectedType () {
            if (!this.filter.type) return null

            return this.filter.type
        },
        contractFilterExists () {
            return parseInt(this.filter.contract)
        },
        productGroupOptions () {
            return this.productGroups.map((item) => {
                return { value: String(item.value), text: item.label, selected: this.filter?.productGroups?.includes(String(item.value)) }
            })
        }
    },
    methods: {
        showActivateContractModal (contract) {
            this.selectedContract = contract
            this.showActivateModal = true
        },
        changePage (page) {
            const parameters = {
                customer: this.customer.id,
                save_filters: true,
                removeAllFilterStatuses: this.removeAllFilterStatusesData
            }

            if (this.filterData) {
                if (this.filterData.status) {
                    parameters.status = this.filterData.status
                }
                if (this.filterData.type) {
                    parameters.type = this.filterData.type
                }
                if (this.filterData.freeText) {
                    parameters.freeText = this.filterData.freeText
                }
                if (this.filterData.showFulfillmentPackage) {
                    parameters.showFulfillmentPackage = this.filterData.showFulfillmentPackage
                }
            }

            if (page !== 1) {
                parameters._query = {
                    page: page
                }
            }

            this.$inertia.get(this.$route('customers.customers.contracts.index', parameters))
        },
        terminateContract (contract) {
            this.selectedContract = contract
            window.axios.get(this.$route('contracts.termination_dates', { contract: contract.id }))
                .then(response => {
                    this.terminationDates = response.data
                })
            this.showTerminationModal = true
        },
        cancelContractTermination (contract) {
            this.contractTermination = contract
            this.showConfirmationModal = true
        },
        applyMoneyBackGuarantee (contract) {
            this.selectedContract = contract
            this.showGuaranteeConfirmationModal = true
        },
        withdrawContract (contract) {
            this.selectedContract = contract
            this.showWithdrawConfirmationModal = true
        },
        clickUnsuspend (contract) {
            this.selectedContract = contract
            this.showUnsuspendConfirmationModal = true
        },
        clickRelease (contract) {
            this.selectedContract = contract
            this.showReleaseConfirmationModal = true
        },
        clickReleaseTree (contract) {
            this.numberOfChildContractWithStatusDraft = 0
            if (contract.children.length > 0) {
                for (const child of contract.children) {
                    this.calcNumberOfChildInDraftStatus(child)
                }
            }

            this.selectedContract = contract
            this.releaseTreeModalMessage = this.__('customers.contracts.release_tree_confirm_message')
                .replace(':contractId', this.selectedContract.id)
                .replace(':numberOfChild', this.numberOfChildContractWithStatusDraft)

            this.showReleaseTreeConfirmationModal = true
        },
        clickDelete (contract) {
            this.selectedContract = contract
            this.deleteModalMessage = this.__('customers.contracts.delete_confirm_message')
                .replace(':contractId', this.selectedContract.id)
                .replace(':productName', this.selectedContract.product_name)

            this.showDeleteConfirmationModal = true
        },
        clickDeleteTree (contract) {
            this.selectedContract = contract
            this.deleteTreeModalErrorMessage = null
            this.deleteTreeModalMessage = null
            this.numberOfChildContractWithPreLiveStatus = 0

            if (this.selectedContract.children.length > 0) {
                for (const child of this.selectedContract.children) {
                    ++this.numberOfChildContractWithPreLiveStatus
                    const match = this.childContractsIsNotInPreLiveStatus(child)

                    if (match !== null) {
                        this.deleteTreeModalErrorMessage = this.__('customers.contracts.delete_tree_error_confirm_message')
                            .replace(':contractId', match.id)
                            .replace(':productName', match.product_name)
                    } else {
                        this.deleteTreeModalMessage = this.__('customers.contracts.delete_tree_confirm_message')
                            .replace(':contractId', this.selectedContract.id)
                            .replace(':numberOfChild', this.numberOfChildContractWithPreLiveStatus)
                    }
                }
            }

            this.showDeleteTreeConfirmationModal = true
        },
        clickRestore (contract) {
            this.selectedContract = contract
            this.showRestoreContractModal = true
        },
        clickAddAddon (contract) {
            this.$inertia.get(this.$route('customers.customers.contracts.create', { customer: contract.customer_id, is_addon: true, contract: contract.id }))
        },
        childContractsIsNotInPreLiveStatus (contract) {
            if (contract.is_pre_live === false) {
                return contract
            }

            if (contract.children.length > 0) {
                for (const child of contract.children) {
                    ++this.numberOfChildContractWithPreLiveStatus
                    const match = this.childContractsIsNotInPreLiveStatus(child)

                    if (match !== null) {
                        return match
                    }
                }
            }

            return null
        },
        calcNumberOfChildInDraftStatus (contract) {
            if (contract.status === 'draft') {
                ++this.numberOfChildContractWithStatusDraft
            }

            if (contract.children.length > 0) {
                for (const child of contract.children) {
                    this.calcNumberOfChildInDraftStatus(child)
                }
            }
        },

        clickSendAuthCode (contract) {
            this.selectedContract = contract
            this.showSendAuthCodeModal = true
        },
        navigateToSSO (contract, type) {
            window.axios.get(this.$route('customers.customers.contracts.sso', {
                customer: this.customer,
                contract: contract,
                type: type
            }))
                .then((response) => {
                    try {
                        window.open(response.data.url, '_blank').focus()
                    } catch (error) {
                        this.showSSOErrorModal = true
                        this.ssoErrorMessage = this.__('customers.contracts.sso_popup_error_message')
                    }
                })
                .catch((err) => {
                    this.loginPageRedirectLink = err.response.data?.login_redirect_link ?? null
                    this.showSSOErrorModal = true
                    this.ssoErrorMessage = (err.response.data.errorMessage || err.response.data.message) ?? this.__('customers.contracts.sso_generation_error_message')
                })
        },
        moveContract (contract) {
            this.selectedContract = contract
            this.showMoveContractModal = true
        },
        terminateContractSilently (contract) {
            this.selectedContract = contract
            this.showTerminateSilentlyModal = true
        },
        cancelContract (contract) {
            this.selectedContract = contract
            this.showCancelContractModal = true
        },
        restoreContract () {
            this.$inertia.post(
                this.$route('customers.customers.contracts.restore-contract', {
                    customer: this.selectedContract.customer_id,
                    contract: this.selectedContract.id
                }),
                {
                    filters: this.filter,
                    redirect_to: 'index',
                    send_withdrawal_email: this.sendWithdrawalEmail
                }
            )
        },
        getParamArray (paramName) {
            const params = new URLSearchParams(window.location.search)
            const paramArray = []
            const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
            for (const param of params.entries()) {
                if (param[0].match(expression)) {
                    paramArray.push(param[1])
                }
            }
            return paramArray
        },
        getParam (paramName) {
            const params = new URLSearchParams(window.location.search)
            return params.has(paramName) ? params.get(paramName) : null
        },
        updateTerminationDates (newDates) {
            this.terminationDates = newDates
        },
        updateSelectedStatuses (values) {
            this.removeAllFilterStatusesData = values.length === 0

            this.filter.status = values
        },
        updateSelectedType (value) {
            if (value.length === 0) {
                delete this.filter.type
            } else {
                this.filter.type = value
            }
        },
        submitFilters () {
            this.$inertia.get(this.$route('customers.customers.contracts.index', {
                customer: this.customer.id,
                _query: this.filter,
                save_filters: true,
                removeAllFilterStatuses: this.removeAllFilterStatusesData
            }))
        },
        resetFilters () {
            this.$inertia.get(this.$route('customers.customers.contracts.index', {
                customer: this.customer.id,
                save_filters: true,
                default_filters: true,
                removeAllFilterStatuses: false
            }))
        },
        selectOrder (value) {
            this.filter.orderNo = value
        },
        closeSsoErrorModal () {
            this.showSSOErrorModal = false
            if (this.loginPageRedirectLink !== null && this.loginPageRedirectLink !== undefined) {
                window.open(this.loginPageRedirectLink, '_blank')
            }
        },
        updateShowFulfillmentPackage (values) {
            this.filter.showFulfillmentPackage = values ? 1 : 0
        },
        updateSelectedProductGroups (values) {
            this.filter.productGroups = values
        },
        closeRestoreTerminationModal () {
            this.showRestoreContractModal = false
            this.sendWithdrawalEmail = false
        }
    }
}
</script>

<style lang="scss" scoped>
.invalid {
    background: rgba(190, 66, 60, 0.07);
}
.contracts-list {
    background: var(--colorAppSurface);
    border-radius: 5px;
    border: 1px solid var(--colorAppBorder);
    display: grid;

    & .item {
        color: inherit;
        text-decoration: inherit;

        &:not(.child):hover {
            background: rgba(0,0,0,0.07);
            cursor: pointer;
        }
        &:not(:last-child) {
            border-bottom: 1px solid var(--colorAppBorder);
        }

        & .children {
            grid-column: 1 / span 6;
            margin: -15px;
            margin-top: 15px;
        }
    }
}
.search {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 10% 1fr;
    grid-gap: 15px;
    align-items: center;
}
.search-btn {
    display: flex;
    justify-content: right;
    grid-gap: 15px;
}
.d-form-row {
    grid-template-columns: 3fr 1fr
}
</style>
